import { memo, ReactNode, useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer, { TableContainerProps } from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import classNames from 'classnames/bind';
import styles from './StyledTable.module.scss';
import { useTheme } from "@mui/system";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

export enum Align {
  center = "center",
  left = "flex-start",
  right = "flex-end",
  inherit = "inherit",
  justify = "justify"
}

export type Field = {
  name: string;
  valueName: string;
  width?: string | number;
  align?: Align;
  render?: (value: any, item: any) => ReactNode;
};

export type PaginationConfigProps = {
  enabled?: boolean;
  rowsPerPageOptions?: number[];
};

export type PaginationProps = {
  rowsPerPage?: number;
  total?: number;
};

export type HeaderProps = {
  enabled?: boolean;
  styles?: React.CSSProperties;
};

export type TableConfig = {
  pagination?: PaginationConfigProps;
  fields: Field[];
  header?: HeaderProps;
};

export interface TableRowData<T> {
  [key: string]: T;
}

interface Props extends TableContainerProps {
  tableConfig: TableConfig;
  tableData: TableRowData<any>[];
  pagination?: PaginationProps;
  onPageChange?: (page: number, limit: number) => void;
  onLimitChange?: (page: number, limit: number) => void;
  onRowClick?: (row: TableRowData<any>) => void;
};

const StyledTable = memo(({ tableConfig, tableData, pagination, onPageChange, onLimitChange, onRowClick, ...rest }: Props) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    setPage(0)
  }, [pagination?.total])

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage + 1, rowsPerPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    if (onLimitChange) {
      onLimitChange(1, newLimit);
    }
  };

  return (
    <TableContainer className={cx("styled-table")} {...rest}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {tableConfig.fields.map((field, index) => (
              <TableCell key={index} width={field.width} sx={{ backgroundColor: theme.palette.table.header }}>
                <Box
                  sx={{
                    ...{
                      display: 'flex',
                      width: '100%',
                      fontWeight: '600',
                      fontSize: '12px',
                      lineHeight: '18px',
                      textWrap: 'nowrap',
                      color: theme.palette.text.primary,
                    }, ...tableConfig.header?.styles
                  }} justifyContent={field.align || Align.left}>{field.name}</Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row, indexRow) => (
            <TableRow key={indexRow} className={cx("table-row")}>
              {tableConfig.fields.map((field, indexField) => {
                return (
                  <TableCell key={indexField} width={field.width}>
                    <Box className={cx("table-col")} justifyContent={field.align || Align.left}>
                      {field.render
                        ? field.render(row[field.valueName], row)
                        : row[field.valueName]}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
        {tableConfig.pagination?.enabled !== false && <TableFooter>
          <TableRow>
            <TablePagination
              className={cx("table-pagination")}
              rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
              count={pagination?.total ?? 0}
              rowsPerPage={rowsPerPage}
              labelRowsPerPage={t('copytrade.common.rows_per_page')}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>}
      </Table>
    </TableContainer>
  );
});

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box className={cx('table-pagination-actions')}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

export default StyledTable;